import { getProductIncludedItems } from '@/lib/productComposition';
import { normalizeMenuBlob } from '@/lib/menuSemanticTags';

export const CATALOG_HERO_FALLBACK_DESC =
  'Explorá nuestro menú y elegí lo que más te guste.';

/** Descripción pública: si solo repite lo de `included_items`, no mostrar de nuevo. */
export function getPublicCatalogDescription(product) {
  const items = getProductIncludedItems(product);
  const desc = String(product?.description ?? '').trim();
  if (!desc) return '';
  if (!items.length) return desc;
  const nd = normalizeMenuBlob(desc).replace(/\s+/g, ' ').trim();
  const ni = normalizeMenuBlob(items.join(', ')).replace(/\s+/g, ' ').trim();
  if (!ni) return desc;
  if (nd === ni) return '';
  const stripped = nd.replace(/^(incluye|incl|viene\s+con|va\s+con|trae|traen)\s+/i, '').trim();
  if (stripped === ni) return '';
  if (ni.length >= 8 && stripped.startsWith(ni) && stripped.length <= ni.length + 3) return '';
  return desc;
}

function demoScoreProduct(p = {}) {
  return (
    (p.demo_featured ? 3 : 0) +
    (Array.isArray(p.demo_tags) && p.demo_tags.includes('bestseller') ? 2 : 0) +
    (p.category_id === 'demo-cat-promos' ? 1 : 0)
  );
}

export function sortCatalogCategories(categories, isDemo) {
  const safe = Array.isArray(categories) ? categories : [];
  return [...safe].sort((a, b) => {
    if (isDemo) {
      if (a?.id === 'demo-cat-promos') return -1;
      if (b?.id === 'demo-cat-promos') return 1;
    }
    const orderA = Number(a?.display_order ?? 0);
    const orderB = Number(b?.display_order ?? 0);
    if (orderA !== orderB) return orderA - orderB;
    return String(a?.name || '').localeCompare(String(b?.name || ''), 'es');
  });
}

export function sortCatalogItemsByPriceAsc(items) {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const priceA = Number(a?.final_price ?? a?.price) || 0;
    const priceB = Number(b?.final_price ?? b?.price) || 0;
    return priceA - priceB;
  });
}

function resolveCatalogItemCategoryId(item) {
  if (!item) return '';
  if (item.is_catalog_promo) {
    const linked = item.catalog_promo_products?.[0];
    return linked?.category_id || linked?.categoryId || '';
  }
  return item.category_id || item.categoryId || '';
}

export function isCatalogFeaturedItem(item) {
  return Boolean(item?.is_daily_deal || item?.is_bestseller || item?.is_catalog_promo);
}

/** Orden fijo: oferta del día → promos → más vendido. */
export function sortCatalogFeaturedItems(items) {
  const rank = (item) => {
    if (item?.is_daily_deal) return 0;
    if (item?.is_catalog_promo) return 1;
    if (item?.is_bestseller) return 2;
    return 3;
  };
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => rank(a) - rank(b));
}

export function partitionCatalogFeaturedItems(products) {
  const featured = [];
  const regular = [];
  for (const item of Array.isArray(products) ? products : []) {
    if (isCatalogFeaturedItem(item)) {
      featured.push(item);
    } else {
      regular.push(item);
    }
  }
  return {
    featured: sortCatalogFeaturedItems(featured),
    regular,
  };
}

export function shouldGroupCatalogByCategory(selectedCategory, searchQuery) {
  return selectedCategory === 'all' && !String(searchQuery || '').trim();
}

/** Agrupa productos/promos por categoría del local; dentro de cada bloque, precio menor a mayor. */
export function groupCatalogProductsByCategory(products, categories, { excludeFeatured = true } = {}) {
  const pool = excludeFeatured ? partitionCatalogFeaturedItems(products).regular : products;
  const safeProducts = Array.isArray(pool) ? pool : [];
  const sortedCategories = sortCatalogCategories(categories, false);
  const categoryById = new Map(sortedCategories.map((cat) => [cat.id, cat]));
  const buckets = new Map(sortedCategories.map((cat) => [cat.id, []]));
  const uncategorized = [];

  for (const item of safeProducts) {
    const categoryId = resolveCatalogItemCategoryId(item);
    if (categoryId && buckets.has(categoryId)) {
      buckets.get(categoryId).push(item);
    } else {
      uncategorized.push(item);
    }
  }

  const sections = sortedCategories
    .map((cat) => ({
      id: cat.id,
      name: cat.name,
      items: sortCatalogItemsByPriceAsc(buckets.get(cat.id) || []),
    }))
    .filter((section) => section.items.length > 0);

  if (uncategorized.length > 0) {
    sections.push({
      id: '__uncategorized__',
      name: 'Otros',
      items: sortCatalogItemsByPriceAsc(uncategorized),
    });
  }

  return sections;
}

export function filterCatalogProducts({
  products,
  selectedCategory,
  searchQuery,
  dailyDealProduct,
  bestsellerProduct,
  isDemo,
}) {
  const safeProducts = Array.isArray(products) ? products : [];
  const query = String(searchQuery || '').toLowerCase();

  const base = safeProducts.filter((p) => {
    if (!p) return false;
    const name = String(p.name || '').toLowerCase();
    const description = String(p.description || '').toLowerCase();
    const categoryId = p.category_id || p.categoryId || '';
    const matchesCategory = selectedCategory === 'all' || categoryId === selectedCategory;
    const matchesSearch = !query || name.includes(query) || description.includes(query);
    return matchesCategory && matchesSearch && p.availability !== 'hidden';
  });

  let sorted = [...base].sort((a, b) => {
    const priceA = Number(a?.final_price || a?.price) || 0;
    const priceB = Number(b?.final_price || b?.price) || 0;
    return priceA - priceB;
  });

  if (isDemo && selectedCategory === 'all' && !query) {
    sorted = [...sorted].sort((a, b) => {
      const ds = demoScoreProduct(b) - demoScoreProduct(a);
      if (ds !== 0) return ds;
      const priceA = Number(a?.final_price || a?.price) || 0;
      const priceB = Number(b?.final_price || b?.price) || 0;
      return priceA - priceB;
    });
  }

  if (dailyDealProduct && selectedCategory === 'all' && !query) {
    const dealId = dailyDealProduct.id;
    const withoutDeal = sorted.filter((p) => p?.id !== dealId);
    sorted = [{ ...dailyDealProduct, is_daily_deal: true }, ...withoutDeal];
  }

  if (bestsellerProduct && selectedCategory === 'all' && !query) {
    const bestId = bestsellerProduct.id;
    const withoutBest = sorted.filter((p) => p?.id !== bestId);
    const dealId = dailyDealProduct?.id;
    const insertAt = dealId && withoutBest[0]?.id === dealId ? 1 : 0;
    const pinned = { ...bestsellerProduct, is_bestseller: true };
    sorted = [...withoutBest.slice(0, insertAt), pinned, ...withoutBest.slice(insertAt)];
  }

  return sorted;
}
